// src/components/MedicationsDashboard.js
// Author: Sunil Jain & Gemini
// FINAL: Implements 7/30-day toggle for consistent compliance view.

import React, { useMemo, useState } from 'react';
import {
    Box, Typography, Paper, Tooltip, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Stack, Chip, IconButton,
    ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { format, parseISO, subDays, eachDayOfInterval, isAfter, startOfDay } from 'date-fns';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ComplianceProgressCircle from './ComplianceProgressCircle';
import DailyDoseTracker from './DailyDoseTracker';

const DateHeader = React.memo(({ adherenceDays }) => {
    if (!adherenceDays || adherenceDays.length === 0) {
        return null;
    }
    const monthLabels = [];
    let lastMonth = -1;
    adherenceDays.forEach(day => {
        const currentMonth = day.getMonth();
        if (currentMonth !== lastMonth) {
            monthLabels.push(format(day, 'MMM'));
            lastMonth = currentMonth;
        } else {
            monthLabels.push(null);
        }
    });
    return (
        <TableHead>
            <TableRow>
                <TableCell sx={{ minWidth: 200, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>Medication</TableCell>
                <TableCell align="center" sx={{ minWidth: 110 }}>Compliance</TableCell>
                <TableCell sx={{minWidth: 120}}>Status</TableCell>
                {adherenceDays.map((day, index) => (
                    <Tooltip key={`header-${day.toISOString()}`} title={format(day, 'MMM d, yyyy')}>
                        <TableCell align="center" sx={{ minWidth: '48px', p: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', height: '1.2em' }}>
                                {monthLabels[index]}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {format(day, 'd')}
                            </Typography>
                        </TableCell>
                    </Tooltip>
                ))}
            </TableRow>
        </TableHead>
    );
});


const MedicationsDashboard = ({ medications = [], isLoading, onDateChange, currentEndDate, isNextDisabled }) => {
    const [compliancePeriod, setCompliancePeriod] = useState(30);

    const weeklyAdherenceDays = useMemo(() => {
        const end = currentEndDate || new Date();
        const start = subDays(end, 6); 
        return eachDayOfInterval({ start, end });
    }, [currentEndDate]);

    const processedMeds = useMemo(() => {
        if (!medications || medications.length === 0) return [];
        
        const periodStartDate = startOfDay(subDays(new Date(), compliancePeriod - 1));

        return medications.map(med => {
            const medCopy = { ...med };
            
            const filteredLogs = medCopy.adherence || [];
            
            let completedCount = 0;
            let possibleCount = 0;
            
            filteredLogs.forEach(log => {
                if (log.status !== 'not_assigned') {
                    possibleCount += (log.doses_required || 0);
                    completedCount += (log.doses_taken || 0);
                }
            });

            // This calculates the % for the selected 7 or 30 day period
            medCopy.adherencePercent = possibleCount > 0 ? (completedCount / possibleCount) * 100 : 0;

            const detailedName = medCopy.item_name_detailed || medCopy.details?.item_name || "Unknown Medication";
            const nameParts = detailedName.split(',');
            medCopy.displayName = nameParts[0];
            medCopy.displayDosage = nameParts.length > 1 ? nameParts.slice(1).join(',').trim() : (medCopy.details?.item_metadata?.dosage || '');
            
            return medCopy;
        });
    }, [medications, compliancePeriod]);

    const sortedMedications = useMemo(() => {
        const getSortOrder = (med) => {
            if (med.status === 'pending_review') return 1;
            if (med.status === 'active') return 2;
            if (med.status === 'stopped') return 3;
            return 99;
        };
        return [...processedMeds].sort((a, b) => {
            const orderA = getSortOrder(a);
            const orderB = getSortOrder(b);
            if (orderA !== orderB) return orderA - orderB;
            return (a.displayName || '').localeCompare(b.displayName || '');
        });
    }, [processedMeds]);
    
    if (isLoading && medications.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    if (!isLoading && medications.length === 0) {
        return <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No medication adherence data to display.</Typography>;
    }

    const firstDay = weeklyAdherenceDays[0];
    const lastDay = weeklyAdherenceDays[weeklyAdherenceDays.length - 1];
    const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt:1, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>Individual Adherence ({compliancePeriod}-Day)</Typography>
                
                <ToggleButtonGroup
                    value={compliancePeriod}
                    exclusive
                    onChange={(e, newVal) => { if (newVal !== null) setCompliancePeriod(newVal); }}
                    aria-label="compliance period"
                    size="small"
                >
                    <ToggleButton value={7} aria-label="7 day view">7-Day</ToggleButton>
                    <ToggleButton value={30} aria-label="30 day view">30-Day</ToggleButton>
                </ToggleButtonGroup>
                
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton size="small" onClick={() => onDateChange('prev')}><ChevronLeftIcon /></IconButton>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: '130px', textAlign: 'center' }}>
                        {`Showing: ${formatDate(firstDay)} → ${formatDate(lastDay)}`}
                    </Typography>
                    <IconButton size="small" onClick={() => onDateChange('next')} disabled={isNextDisabled}><ChevronRightIcon /></IconButton>
                </Stack>
            </Box>
            <Paper variant="outlined">
                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table size="small" stickyHeader>
                        <DateHeader adherenceDays={weeklyAdherenceDays} />
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center"><CircularProgress /></TableCell>
                                </TableRow>
                            )}
                            {sortedMedications.map((med) => {
                                const logsByDate = new Map((med.daily_doses || []).map(log => [format(parseISO(log.date), 'yyyy-MM-dd'), log]));
                                return (
                                    <TableRow key={med.patient_regimen_id || med.id} hover>
                                        <TableCell sx={{ minWidth: 200, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                            <ListItemText
                                                primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{med.displayName}</Typography>}
                                                secondary={med.displayDosage || 'N/A'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <ComplianceProgressCircle value={med.adherencePercent} />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Chip label={med.status ? med.status.replace(/_/g, ' ') : 'Unknown'} size="small" color={med.status === 'active' ? 'success' : (med.status === 'pending_review' ? 'warning' : 'default')} sx={{textTransform: 'capitalize'}} />
                                                {med.details?.item_metadata?.source === 'PATIENT_ADDED' && <Chip label="Self-Reported" size="small" variant="outlined" color="warning" />}
                                            </Stack>
                                        </TableCell>
                                        {weeklyAdherenceDays.map(headerDay => {
                                            const dayLog = logsByDate.get(format(headerDay, 'yyyy-MM-dd'));
                                            return (
                                                <TableCell key={`${med.patient_regimen_id}-${headerDay.toISOString()}`} sx={{ p: 0 }}>
                                                    <DailyDoseTracker day={dayLog || { status: 'not_assigned', date: format(headerDay, 'yyyy-MM-dd') }} />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default MedicationsDashboard;